import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography } from '@mui/material';
import MarketScanner from '../../../components/MarketScanner';
import {
  fetchScannerData,
  addToWatchlist,
  removeFromWatchlist,
  buildScannerSymbolsKey,
} from '../marketsSlice';

/**
 * ScannerWidget - Wrapper for MarketScanner component
 *
 * Drives the scanner table from the user's watchlist. The "Live events"
 * panel on the markets page is meant to show events for *every* symbol
 * the user is tracking, so we read the watchlist directly from Redux and
 * refetch scanner price data whenever it changes.
 *
 * Exposes a `refresh()` method via ref so the page-level refresh button
 * can ask for fresh prices without dispatching its own fetchScannerData
 * (which would race against the widget's effect-driven fetch and could
 * stomp on it if the stale call resolved last).
 *
 * Always fills the parent container's height. Designed to be wrapped
 * in a Panel that provides the height constraint via grid stretching.
 */
const ScannerWidget = forwardRef((_props, ref) => {
  const dispatch = useDispatch();
  const scannerData = useSelector((state) => state.markets.scannerData);
  const scannerLoading = useSelector((state) => state.markets.scannerLoading);
  const scannerError = useSelector((state) => state.markets.scannerError);
  const scannerLastFetchedSymbolsKey = useSelector(
    (state) => state.markets.scannerLastFetchedSymbolsKey,
  );
  const allRecentEvents = useSelector((state) => state.markets.allRecentEvents);
  const watchlist = useSelector((state) => state.markets.watchlist);
  const watchlistLoading = useSelector((state) => state.markets.watchlistLoading);
  const watchlistLastFetched = useSelector((state) => state.markets.watchlistLastFetched);
  const watchlistError = useSelector((state) => state.markets.watchlistError);

  // Normalize watchlist symbols to the hyphen form used everywhere else in
  // the scanner pipeline (event symbols, SYMBOL_NAMES lookup, market-data
  // URL path). Memoize so the useEffect below only refires on actual
  // membership changes — without this, every Redux update produces a new
  // array reference and triggers a redundant fetch.
  const symbols = useMemo(
    () => (watchlist || []).map((s) => s.replace('/', '-')),
    [watchlist],
  );
  // Stable, order-independent string key. Used both as the effect dep
  // (only refire on actual membership changes) and to compare against the
  // cache key the slice records on each fulfilled fetch (so we can tell
  // whether we already have data for the current set). Use the slice's
  // key builder rather than rolling our own — the comparison against
  // `scannerLastFetchedSymbolsKey` only works if both sides use the
  // exact same algorithm.
  const symbolsKey = useMemo(() => buildScannerSymbolsKey(symbols), [symbols]);

  useEffect(() => {
    if (symbols.length === 0) {return;}
    dispatch(fetchScannerData({ symbols }));
    // symbolsKey is the stable identity of `symbols`; the linter doesn't
    // know that, but adding `symbols` directly would refire on every
    // render that produced a new array reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, symbolsKey]);

  // Page-level refresh hook. The page button calls this rather than
  // dispatching fetchScannerData itself, which avoided the race where the
  // page passed a stale local watchlist while fetchWatchlist was in
  // flight — a late-arriving stale fetch could stomp the widget's
  // correct one and leave the cache key permanently mismatched.
  const refresh = useCallback(() => {
    if (symbols.length === 0) {return;}
    dispatch(fetchScannerData({ symbols, force: true }));
  }, [dispatch, symbols]);

  useImperativeHandle(ref, () => ({ refresh }), [refresh]);

  // Handle watchlist toggle from MarketScanner
  const handleWatchlistToggle = useCallback((symbol, shouldAdd) => {
    if (shouldAdd) {
      dispatch(addToWatchlist(symbol));
    } else {
      dispatch(removeFromWatchlist(symbol));
    }
  }, [dispatch]);

  // The scanner is loading until we have authoritative watchlist data AND
  // (if there's anything to fetch) the price data has resolved. Without
  // gating on `watchlistLastFetched`, an empty initial Redux state would
  // briefly show "no data" before fetchWatchlist resolves.
  //
  // We also treat "fetch errored" as ready — without this, a rejected
  // watchlist fetch would leave the panel trapped in a permanent
  // skeleton (same trap that BriefingPanel guards against).
  const watchlistFetched =
    watchlistLastFetched !== null && watchlistLastFetched !== undefined;
  const watchlistFailed = watchlistError !== null && watchlistError !== undefined;
  const watchlistReady = !watchlistLoading && (watchlistFetched || watchlistFailed);
  // After the watchlist resolves there's a single render before the
  // useEffect dispatches fetchScannerData — `scannerLoading` is still
  // false at that moment, which would otherwise let the table render
  // empty rows for one frame. Compare the slice's last-fetched key
  // against the current symbol set so we keep the loading skeleton up
  // until we actually have data for *these* symbols.
  const scannerHasDataForCurrentSymbols = scannerLastFetchedSymbolsKey === symbolsKey;
  // A scanner fetch error breaks out of loading — without this gate the
  // skeleton would stay up forever after a rejected fetch (the rejected
  // handler doesn't update scannerLastFetchedSymbolsKey, so
  // scannerHasDataForCurrentSymbols stays false). Same trap pattern as
  // the watchlist-error case above.
  const scannerHasFailed = scannerError !== null && scannerError !== undefined;
  const isLoading =
    !watchlistReady ||
    (symbols.length > 0 &&
      !scannerHasFailed &&
      (scannerLoading || !scannerHasDataForCurrentSymbols));

  // Empty-state message when the user genuinely has no watchlist. The
  // BriefingPanel handles the "use suggested defaults" CTA, so we keep
  // this lighter-weight. Checked *before* the error fallback because a
  // stale error from a previous non-empty state shouldn't block the
  // empty-state CTA — the error gets cleared automatically the next
  // time the watchlist becomes non-empty (the effect dispatches a new
  // fetch which clears the error in pending).
  if (watchlistReady && symbols.length === 0) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: 4,
        }}
      >
        <Typography
          sx={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.8rem',
            color: 'var(--dwlf-ink-45)',
            textAlign: 'center',
            maxWidth: 320,
            lineHeight: 1.7,
          }}
        >
          No symbols on your watchlist yet. Add some to start seeing live
          events here.
        </Typography>
      </Box>
    );
  }

  // Scanner fetch failed with a non-empty watchlist — show an error
  // message instead of the table (which would otherwise render rows full
  // of dashes from empty priceData) and offer a retry via the same
  // refresh path. The empty-watchlist branch above takes precedence so
  // a stale error never traps the user on a dead-end screen.
  if (scannerHasFailed && !scannerLoading) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1.5,
          px: 3,
          py: 4,
        }}
      >
        <Typography
          sx={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: '0.8rem',
            color: 'rgba(248, 81, 73, 0.8)',
            textAlign: 'center',
          }}
        >
          Couldn&apos;t load scanner data.
        </Typography>
        <Box
          component="button"
          onClick={refresh}
          sx={{
            px: 2,
            py: 0.75,
            backgroundColor: 'transparent',
            border: '1px solid var(--dwlf-accent-40)',
            color: 'var(--dwlf-accent)',
            fontFamily: '"Space Mono", monospace',
            fontSize: '0.65rem',
            fontWeight: 500,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'var(--dwlf-accent-08)',
              borderColor: 'var(--dwlf-accent)',
            },
          }}
        >
          Retry
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <MarketScanner
        symbols={symbols}
        priceData={scannerData}
        recentEvents={allRecentEvents}
        watchlist={watchlist}
        onWatchlistToggle={handleWatchlistToggle}
        loading={isLoading}
        hideWatchlistFilter
        removeOnly
      />
    </Box>
  );
});

ScannerWidget.displayName = 'ScannerWidget';

export default ScannerWidget;
